import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { geoMercator, geoNaturalEarth1, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import { ChevronDown, Globe2, MapPin, X } from 'lucide-react';
import { analyticsDb } from '../../firebase';
import { NAME_BY_ALPHA2, NUMERIC_BY_ALPHA2 } from '../../utils/isoNumeric';

// Home page "Devices by location" (owner 2026-10-30): a drawn world map - not a street map - with every device
// except DELETED counted by country and city, online in orange and offline in gray (offline includes devices off for
// more than 14 days). The city comes from the device's internet address (DB-IP Lite, see docs/MDM-ANALYTICS.md), so
// it is the provider's city, not the venue: a picture of where the fleet is, nothing to click through to.
// Data: fleet/summary.geo (one document, written by the fleet scan). Shapes: public/geo/countries-50m.json.

const BRAND = '#ff7a1f'; // online
const OFF = '#9CA3AF'; // offline
const LAND = '#262626';
const LAND_EDGE = 'rgba(255,255,255,0.07)';
const SEA = '#141414';
const RING = '#181818'; // surface-inset - the 2px gap around a bubble
const TEXT = '#D1D5DB';

const FILTERS = [['all', 'All'], ['online', 'Online'], ['offline', 'Offline']];

let shapesPromise = null;
const loadShapes = () => {
  if (!shapesPromise) {
    shapesPromise = fetch('/geo/countries-50m.json')
      .then((r) => r.json())
      .then((topo) => feature(topo, topo.objects.countries).features)
      .catch((e) => { shapesPromise = null; throw e; });
  }
  return shapesPromise;
};

const countryShape = (shapes, cc) => {
  const id = NUMERIC_BY_ALPHA2[cc];
  return shapes.find((f) => (id && f.id === id) || (NAME_BY_ALPHA2[cc] && f.properties.name === NAME_BY_ALPHA2[cc]));
};

// Size of the map box. A callback ref, because the box appears only once the summary has loaded - a plain ref
// read on mount would stay empty and the map would never draw (2026-10-30: stuck on "Loading map…").
function useSize() {
  const [node, setNode] = useState(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!node) return undefined;
    const ro = new ResizeObserver(([e]) => setSize({ w: Math.round(e.contentRect.width), h: Math.round(e.contentRect.height) }));
    ro.observe(node);
    return () => ro.disconnect();
  }, [node]);
  return [setNode, size, node];
}

// Box around the cities, never tighter than ~10 x 6 degrees so a fleet in one city still shows its region.
function regionBox(cities) {
  if (!cities.length) return null;
  let [x0, y0, x1, y1] = [180, 90, -180, -90];
  cities.forEach((c) => { x0 = Math.min(x0, c.lon); x1 = Math.max(x1, c.lon); y0 = Math.min(y0, c.lat); y1 = Math.max(y1, c.lat); });
  const grow = (lo, hi, min) => {
    const span = Math.max(hi - lo, min) * 1.25;
    const mid = (lo + hi) / 2;
    return [mid - span / 2, mid + span / 2];
  };
  [x0, x1] = grow(x0, x1, 10);
  [y0, y1] = grow(y0, y1, 6);
  return { type: 'MultiPoint', coordinates: [[x0, y0], [x1, y1], [x0, y1], [x1, y0]] };
}

// Online share of a bubble as a wedge from 12 o'clock, clockwise.
function wedge(x, y, r, frac) {
  const a = frac * 2 * Math.PI;
  const ex = x + r * Math.sin(a);
  const ey = y - r * Math.cos(a);
  return `M${x},${y} L${x},${y - r} A${r},${r} 0 ${frac > 0.5 ? 1 : 0} 1 ${ex},${ey} Z`;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const flagUrl = (cc) => `https://flagcdn.com/24x18/${cc}.png`;
// count for the chosen filter (summaries written before 2026-10-30 have no `offline`)
const countOf = (x, filter) => {
  if (filter === 'online') return x.online || 0;
  if (filter === 'offline') return x.offline ?? Math.max(0, (x.total || 0) - (x.online || 0));
  return x.total || 0;
};

function Flag({ cc }) {
  return (
    <img
      src={flagUrl(cc)}
      alt=""
      className="w-[18px] h-[13px] rounded-[2px] object-cover shrink-0"
      onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
    />
  );
}

function Segmented({ value, options, onChange, label }) {
  return (
    <div className="inline-flex rounded-lg border border-white/[0.08] p-0.5 bg-surface-inset" role="group" aria-label={label}>
      {options.map(([v, text]) => (
        <button
          key={v}
          type="button"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${value === v ? 'bg-brand/15 text-brand-400' : 'text-gray-400 hover:text-white'}`}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

export default function DeviceMap({ summary: summaryProp }) {
  const [boxRef, { w, h }, boxNode] = useSize();
  const [live, setLive] = useState(undefined);
  const [shapes, setShapes] = useState(null);
  const [view, setView] = useState('region'); // 'region' | 'world' | country code
  const [filter, setFilter] = useState('all'); // 'all' | 'online' | 'offline'
  const [open, setOpen] = useState('');
  const [hover, setHover] = useState(null); // { x, y, title, text }
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (summaryProp) return undefined;
    return onSnapshot(doc(analyticsDb, 'fleet', 'summary'), (s) => setLive(s.exists() ? s.data() : null), () => setLive(null));
  }, [summaryProp]);
  useEffect(() => { loadShapes().then(setShapes).catch(() => setShapes([])); }, []);

  const summary = summaryProp || live;
  const geo = summary?.geo;
  const allCountries = useMemo(() => geo?.countries || [], [geo]);
  const allCities = useMemo(
    () => allCountries.flatMap((c) => c.cities.map((city) => ({ ...city, cc: c.cc, country: c.name }))),
    [allCountries],
  );
  // what the filter shows: places with at least one device of that kind, biggest first
  const countries = useMemo(
    () => allCountries.filter((c) => countOf(c, filter) > 0).sort((a, b) => countOf(b, filter) - countOf(a, filter)),
    [allCountries, filter],
  );
  const cities = useMemo(() => allCities.filter((c) => countOf(c, filter) > 0), [allCities, filter]);
  const byCc = useMemo(() => Object.fromEntries(allCountries.map((c) => [c.cc, c])), [allCountries]);
  const narrow = w > 0 && w < 520;

  // The view frames all places whatever the filter, so switching Online / Offline does not move the map.
  const projection = useMemo(() => {
    if (!w || !h || !shapes) return null;
    const pad = narrow ? 14 : 24;
    const extent = [[pad, pad], [w - pad, h - pad]];
    if (view === 'world') return geoNaturalEarth1().fitExtent([[6, 6], [w - 6, h - 6]], { type: 'Sphere' });
    const shape = view !== 'region' && countryShape(shapes, view);
    if (shape) return geoMercator().fitExtent(extent, shape);
    const box = regionBox(allCities);
    return box ? geoMercator().fitExtent(extent, box) : geoNaturalEarth1().fitExtent([[6, 6], [w - 6, h - 6]], { type: 'Sphere' });
  }, [w, h, shapes, view, allCities, narrow]);

  const paths = useMemo(() => {
    if (!projection || !shapes) return [];
    const path = geoPath(projection);
    const idToCc = Object.fromEntries(allCountries.map((c) => [NUMERIC_BY_ALPHA2[c.cc] || NAME_BY_ALPHA2[c.cc], c.cc]));
    // index in the key: some shapes share an id (036 = Australia and Ashmore Is.)
    return shapes.map((f, i) => ({ key: `${f.id || f.properties.name}-${i}`, d: path(f), cc: idToCc[f.id] || idToCc[f.properties.name] || null }))
      .filter((p) => p.d);
  }, [projection, shapes, allCountries]);

  const maxCountry = Math.max(1, ...countries.map((c) => countOf(c, filter)));
  // World view: one bubble per country (at the device-weighted middle of its cities) - cities would pile up.
  const points = useMemo(() => (view === 'world'
    ? countries.filter((c) => c.cities.length).map((c) => {
      const n = c.cities.reduce((a, x) => a + x.total, 0);
      return {
        ...c, country: '', lat: c.cities.reduce((a, x) => a + x.lat * x.total, 0) / n,
        lon: c.cities.reduce((a, x) => a + x.lon * x.total, 0) / n,
      };
    })
    : cities), [view, countries, cities]);
  const maxPoint = Math.max(1, ...points.map((c) => countOf(c, filter)));
  const rMax = view === 'world' ? (narrow ? 11 : 14) : (narrow ? 15 : 20);

  const bubbles = useMemo(() => {
    if (!projection) return [];
    return points.map((c) => {
      const p = projection([c.lon, c.lat]);
      const n = countOf(c, filter);
      return p ? { ...c, n, x: p[0], y: p[1], r: 4 + (rMax - 4) * Math.sqrt(n / maxPoint) } : null;
    }).filter((b) => b && b.x > -30 && b.y > -30 && b.x < w + 30 && b.y < h + 30)
      .sort((a, b) => b.n - a.n);
  }, [projection, points, filter, w, h, maxPoint, rMax]);

  // Names for the biggest places in view, skipping any that would overlap a name already placed.
  const labels = useMemo(() => {
    const placed = [];
    const out = [];
    bubbles.slice(0, narrow ? 4 : 7).forEach((b, i) => {
      const width = b.name.length * 6.4 + 6;
      const box = { x0: b.x + b.r + 4, y0: b.y - 8, x1: b.x + b.r + 4 + width, y1: b.y + 8 };
      if (box.x1 > w - 4) { box.x0 = b.x - b.r - 4 - width; box.x1 = b.x - b.r - 4; }
      // the biggest place is always named (its halo keeps it readable over small neighbours)
      const hit = placed.some((p) => !(box.x1 < p.x0 || box.x0 > p.x1 || box.y1 < p.y0 || box.y0 > p.y1))
        || (i > 0 && bubbles.some((o) => o !== b && o.x + o.r > box.x0 && o.x - o.r < box.x1 && o.y + o.r > box.y0 && o.y - o.r < box.y1));
      if (!hit) { placed.push(box); out.push({ ...b, lx: box.x0, anchorEnd: box.x1 === b.x - b.r - 4 }); }
    });
    return out;
  }, [bubbles, narrow, w]);

  // Counts inside bubbles: biggest first, skipping one that would print over a bigger place's number.
  const numbered = [];
  bubbles.filter((b) => b.r >= 10).forEach((b) => {
    const width = String(b.n).length * 7 + 4;
    if (!numbered.some((o) => Math.abs(o.x - b.x) < (width + String(o.n).length * 7 + 4) / 2 && Math.abs(o.y - b.y) < 14)) numbered.push(b);
  });

  if (summary === undefined || !geo) return null;

  const unknown = typeof geo.unknown === 'number' ? { total: geo.unknown } : (geo.unknown || {});
  const located = countries.reduce((a, c) => a + countOf(c, filter), 0);
  const everyone = allCountries.reduce((a, c) => a + countOf(c, filter), 0) + countOf(unknown, filter);
  const selected = view !== 'region' && view !== 'world' ? byCc[view] : null;
  const kind = filter === 'all' ? 'device' : `${filter} device`;

  const describe = (x) => {
    const off = countOf(x, 'offline');
    const parts = [plural(x.total, 'device', 'devices'), `${x.online} online`, `${off} offline`];
    if (x.longOff) parts.push(`${x.longOff} of them off for more than 14 days`);
    return parts.join(' · ');
  };
  const showTip = (e, title, x) => {
    const r = boxNode.getBoundingClientRect();
    setHover({ x: e.clientX - r.left, y: e.clientY - r.top, title, text: describe(x) });
  };
  const pickCountry = (cc) => {
    setView(view === cc ? 'region' : cc);
    setOpen(view === cc ? '' : cc);
  };
  const subline = (c) => {
    if (filter === 'online') return `of ${plural(c.total, 'device', 'devices')}`;
    if (filter === 'offline') return c.longOff ? `${c.longOff} off for more than 14 days` : `of ${plural(c.total, 'device', 'devices')}`;
    return `${c.online} online · ${countOf(c, 'offline')} offline`;
  };

  return (
    <section className="card-chrome mb-6">
      <header className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="brand-icon-badge"><Globe2 size={16} /></div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-white">Devices by location</h3>
            <p className="text-xs text-gray-500">
              {located} of {plural(everyone, kind, `${kind}s`)} located
              {' · '}{plural(countries.length, 'country', 'countries')} · {cities.length} {cities.length === 1 ? 'city' : 'cities'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:ml-auto">
          {selected && (
            <span className="brand-pill inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium">
              <Flag cc={selected.cc} /> {selected.name}
              <button type="button" aria-label="Back to the region" onClick={() => { setView('region'); setOpen(''); }} className="rounded-full p-0.5 hover:bg-white/10">
                <X size={12} />
              </button>
            </span>
          )}
          <Segmented value={filter} options={FILTERS} onChange={setFilter} label="Devices shown" />
          <Segmented value={view === 'world' ? 'world' : 'region'} options={[['region', 'Region'], ['world', 'World']]} onChange={(v) => { setView(v); setOpen(''); }} label="Map view" />
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <div
            ref={boxRef}
            className="relative h-[250px] sm:h-[340px] lg:h-[400px] rounded-xl overflow-hidden border border-white/[0.06]"
            style={{ background: SEA }}
            onMouseLeave={() => setHover(null)}
          >
            {projection && (
              <svg key={view} width={w} height={h} className="block" role="img" aria-label={`Map of ${located} ${kind}s by country and city`}>
                {paths.map((p) => {
                  const c = p.cc && byCc[p.cc];
                  const n = c ? countOf(c, filter) : 0;
                  // one hue, light -> strong with the share of devices; kept low so the city bubbles stand out
                  const t = n ? 0.1 + 0.28 * Math.sqrt(n / maxCountry) : 0;
                  return (
                    <path
                      key={p.key}
                      d={p.d}
                      fill={n ? (filter === 'offline' ? OFF : BRAND) : LAND}
                      fillOpacity={n ? t : 1}
                      stroke={n ? (filter === 'offline' ? 'rgba(156,163,175,0.45)' : 'rgba(255,122,31,0.45)') : LAND_EDGE}
                      strokeWidth={n ? 0.8 : 0.5}
                      className={n ? 'cursor-pointer' : ''}
                      onMouseMove={n ? (e) => showTip(e, c.name, c) : () => setHover(null)}
                      onClick={n ? () => pickCountry(c.cc) : undefined}
                    />
                  );
                })}
                {bubbles.map((b) => {
                  const onFrac = b.total ? b.online / b.total : 0;
                  const title = b.country ? `${b.name}, ${b.country}` : b.name;
                  return (
                    <g key={`${b.cc}-${b.name}`} className="cursor-pointer" onMouseMove={(e) => showTip(e, title, b)} onClick={(e) => showTip(e, title, b)}>
                      <circle cx={b.x} cy={b.y} r={b.r + 6} fill="transparent" />
                      {filter === 'all' ? (
                        <>
                          {/* gray = offline share, orange wedge = online share */}
                          <circle cx={b.x} cy={b.y} r={b.r} fill={onFrac >= 1 ? BRAND : OFF} stroke={RING} strokeWidth={2} />
                          {onFrac > 0 && onFrac < 1 && <path d={wedge(b.x, b.y, b.r - 1, onFrac)} fill={BRAND} />}
                        </>
                      ) : (
                        <circle cx={b.x} cy={b.y} r={b.r} fill={filter === 'offline' ? OFF : BRAND} stroke={RING} strokeWidth={2} />
                      )}
                    </g>
                  );
                })}
                {/* counts above every bubble, so a big city's number is never covered by its small neighbours */}
                {numbered.map((b) => (
                  <text
                    key={`n-${b.cc}-${b.name}`}
                    x={b.x}
                    y={b.y}
                    dy="0.35em"
                    textAnchor="middle"
                    fontSize={b.r >= 14 ? 12 : 10}
                    fontWeight={700}
                    fill="#fff"
                    stroke="#1a1a1a"
                    strokeOpacity={0.55}
                    strokeWidth={2.5}
                    paintOrder="stroke"
                    pointerEvents="none"
                  >
                    {b.n}
                  </text>
                ))}
                {labels.map((l) => (
                  <text
                    key={`label-${l.cc}-${l.name}`}
                    x={l.anchorEnd ? l.x - l.r - 4 : l.lx}
                    y={l.y}
                    dy="0.35em"
                    textAnchor={l.anchorEnd ? 'end' : 'start'}
                    fontSize={11}
                    fontWeight={500}
                    fill={TEXT}
                    stroke={SEA}
                    strokeWidth={3}
                    paintOrder="stroke"
                    pointerEvents="none"
                  >
                    {l.name}
                  </text>
                ))}
              </svg>
            )}
            {!projection && <div className="absolute inset-0 grid place-items-center text-xs text-gray-500">Loading map…</div>}
            {projection && (
              <div className="absolute left-2 bottom-2 flex items-center gap-3 rounded-md bg-black/50 px-2 py-1 text-[11px] text-gray-300 pointer-events-none">
                {filter !== 'offline' && <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: BRAND }} /> Online</span>}
                {filter !== 'online' && <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: OFF }} /> Offline</span>}
              </div>
            )}
            {hover && (
              <div
                className="absolute z-10 pointer-events-none rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-2 shadow-lg max-w-[260px]"
                style={{ left: Math.min(hover.x + 12, Math.max(0, w - 270)), top: Math.max(4, hover.y - 56) }}
              >
                <p className="text-xs font-semibold text-white">{hover.title}</p>
                <p className="text-[11px] text-gray-400">{hover.text}</p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 min-w-0">
          {/* Desktop: the list scrolls inside the map's height. Phone: first 6 + "Show all" (no scroll inside scroll). */}
          <div className="space-y-2 lg:h-[400px] lg:overflow-y-auto lg:pr-1">
            {countries.map((c, i) => {
              const isOpen = open === c.cc;
              const n = countOf(c, filter);
              const share = Math.round((n / Math.max(1, located)) * 100);
              const onPart = filter === 'all' && c.total ? (c.online / c.total) * share : 0;
              return (
                <div key={c.cc} className={`rounded-xl border transition-colors ${!showAll && i >= 6 ? 'hidden lg:block' : ''} ${isOpen ? 'border-brand/50 bg-brand/[0.06]' : 'border-white/[0.06] bg-surface-inset'}`}>
                  <button
                    type="button"
                    onClick={() => pickCountry(c.cc)}
                    aria-expanded={isOpen}
                    className="w-full text-left px-3 py-2.5 flex items-center gap-3"
                  >
                    <Flag cc={c.cc} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-sm font-semibold text-white truncate">{c.name}</p>
                        <p className="text-base font-bold text-white shrink-0">{n}</p>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[11px] text-gray-500 truncate">{subline(c)}</p>
                        <p className="text-[11px] text-gray-500 shrink-0">{share}%</p>
                      </div>
                      <div className="mt-1.5 h-1 rounded-full bg-white/[0.06] overflow-hidden flex">
                        {filter === 'all' ? (
                          <>
                            <div className="h-full" style={{ width: `${onPart}%`, background: BRAND }} />
                            <div className="h-full" style={{ width: `${Math.max(onPart ? 0 : 2, share - onPart)}%`, background: OFF }} />
                          </>
                        ) : (
                          <div className="h-full rounded-full" style={{ width: `${Math.max(2, share)}%`, background: filter === 'offline' ? OFF : BRAND }} />
                        )}
                      </div>
                    </div>
                    <ChevronDown size={16} className={`text-gray-500 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <ul className="px-3 pb-3 space-y-1">
                      {c.cities.filter((city) => countOf(city, filter) > 0).slice(0, 10).map((city) => (
                        <li key={city.name} className="flex items-center gap-2 text-xs">
                          <MapPin size={12} className={`shrink-0 ${filter === 'offline' ? 'text-gray-400' : 'text-brand-400'}`} />
                          <span className="text-gray-300 truncate flex-1">{city.name}</span>
                          {filter === 'all' && <span className="text-gray-500 shrink-0">{city.online} online</span>}
                          <span className="text-white font-semibold w-8 text-right shrink-0">{countOf(city, filter)}</span>
                        </li>
                      ))}
                      {c.cities.filter((city) => countOf(city, filter) > 0).length > 10 && (
                        <li className="text-[11px] text-gray-500 pl-5">
                          + {plural(c.cities.filter((city) => countOf(city, filter) > 0).length - 10, 'more place', 'more places')}
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              );
            })}
            {countries.length > 6 && (
              <button type="button" onClick={() => setShowAll(!showAll)} className="lg:hidden btn-secondary w-full justify-center">
                <ChevronDown size={16} className={showAll ? 'rotate-180' : ''} />
                {showAll ? 'Show fewer countries' : `Show all ${countries.length} countries`}
              </button>
            )}
            {countries.length === 0 && <p className="text-sm text-gray-500 py-6 text-center">No {kind}s with a known location.</p>}
          </div>
        </div>
      </div>

      <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
        Approximate: the city of the device&apos;s internet provider, not the venue address. Offline includes devices
        switched off for more than 14 days; the DELETED group is not shown.
        {countOf(unknown, filter) ? ` ${plural(countOf(unknown, filter), `${kind} has`, `${kind}s have`)} no public address and ${countOf(unknown, filter) === 1 ? 'is' : 'are'} not on the map.` : ''}
        {' '}
        <a href="https://db-ip.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white underline-offset-2 hover:underline">
          IP Geolocation by DB-IP
        </a>
      </p>
    </section>
  );
}
